import React, { useRef, useEffect } from "react";
import {
  PhotoPermissionsFilter,
  PermissionValue,
  DynamicPermissionFilter
} from "./filterTypes";
import LoadingSpinner from "../components/LoadingSpinner";

interface PhotoPermissionsDropdownProps {
  value: PhotoPermissionsFilter;
  onChange: (updated: PhotoPermissionsFilter, loadingKey?: string) => void;
  onClose: () => void;
  availableAttributes: Array<{ attributeId: number; attributeLabel: string }>;
  showRedacted?: boolean;
  isLoading?: boolean;
  loadingKey?: string | null;
}

const OPTIONS: PermissionValue[] = ["yes", "no", "all"];

const PhotoPermissionsDropdown: React.FC<PhotoPermissionsDropdownProps> = ({
  value,
  onChange,
  onClose,
  availableAttributes,
  showRedacted = false,
  isLoading = false,
  loadingKey = null
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;

      if (ref.current?.contains(target)) return;

      if (target instanceof Element && target.closest(".mobile-filters-modal"))
        return;

      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const setPermission = (
    attributeId: number,
    attributeLabel: string,
    newValue: PermissionValue
  ) => {
    let updatedPermissions = [...value.permissions];
    const existingIndex = updatedPermissions.findIndex(
      p => p.attributeId === attributeId
    );

    if (newValue === "all") {
      if (existingIndex >= 0) {
        updatedPermissions.splice(existingIndex, 1);
      }
    } else {
      if (existingIndex >= 0) {
        updatedPermissions[existingIndex] = {
          attributeId,
          attributeLabel,
          value: newValue
        };
      } else {
        updatedPermissions.push({
          attributeId,
          attributeLabel,
          value: newValue
        });
      }
    }

    onChange(
      { ...value, permissions: updatedPermissions },
      `${attributeId}-${newValue}`
    );
  };

  const setRedactedPhoto = (newValue: PermissionValue) => {
    onChange({ ...value, redactedPhoto: newValue }, `redacted-${newValue}`);
  };

  const getPermissionValue = (attributeId: number): PermissionValue => {
    const permission = value.permissions.find(
      p => p.attributeId === attributeId
    );
    return permission?.value || "all";
  };

  const renderOptionButtons = (
    currentValue: PermissionValue,
    keyPrefix: string,
    onSelect: (option: PermissionValue) => void
  ) => {
    return OPTIONS.map(option => {
      const isLoadingThis =
        isLoading && loadingKey === `${keyPrefix}-${option}`;
      return (
        <button
          key={option}
          className={`search-filter-dropdown__seg-btn${
            currentValue === option
              ? " search-filter-dropdown__seg-btn--active"
              : ""
          }`}
          onClick={() => onSelect(option)}
          disabled={isLoadingThis}
        >
          {isLoadingThis ? (
            <LoadingSpinner size="small" position="before" />
          ) : (
            option.charAt(0).toUpperCase() + option.slice(1)
          )}
        </button>
      );
    });
  };

  return (
    <div
      className="search-filter-dropdown search-filter-dropdown--permissions"
      ref={ref}
    >
      {availableAttributes.map(({ attributeId, attributeLabel }) => (
        <div key={attributeId} className="search-filter-dropdown__row">
          <span className="search-filter-dropdown__row-label">
            {attributeLabel}
          </span>
          <div className="search-filter-dropdown__segmented">
            {renderOptionButtons(
              getPermissionValue(attributeId),
              `${attributeId}`,
              option => setPermission(attributeId, attributeLabel, option)
            )}
          </div>
        </div>
      ))}

      {showRedacted && (
        <div className="search-filter-dropdown__row">
          <span className="search-filter-dropdown__row-label">
            Redacted photo
          </span>
          <div className="search-filter-dropdown__segmented">
            {renderOptionButtons(
              value.redactedPhoto,
              "redacted",
              setRedactedPhoto
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoPermissionsDropdown;
